import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Star, Download, Shield, Zap } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { GooglePlayIcon } from './GooglePlayIcon';
import { AppleAppStoreIcon } from './AppleAppStoreIcon';

export const AppDownloadSection: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  const handleAppStoreDownload = () => {
    console.log('Redirecting to Apple App Store...');
    // In production: window.open('https://apps.apple.com/app/octify', '_blank');
  };

  const handleGooglePlayDownload = () => {
    console.log('Redirecting to Google Play Store...');
    // In production: window.open('https://play.google.com/store/apps/details?id=com.octify.app', '_blank');
  };

  const features = [
    {
      icon: Zap,
      title: 'AI-Powered Insights',
      description: 'Get personalized relationship advice powered by Gemini 2.5 Flash'
    },
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'Your data is encrypted and never shared with third parties'
    },
    {
      icon: Star,
      title: 'Proven Results',
      description: '94% of users report improved relationships within 30 days'
    }
  ];

  return (
    <section id="download" className="py-24 bg-gradient-to-br from-orange-50 via-blue-50 to-purple-50 dark:from-orange-900/10 dark:via-blue-900/10 dark:to-purple-900/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-orange-500/10 text-orange-600 dark:text-orange-400 rounded-full text-sm font-medium mb-6">
              <Smartphone className="w-4 h-4 mr-2" />
              Available Now
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Download the{' '}
              <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                Octify App
              </span>
            </h2>
            
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              Transform your relationships with AI-powered insights, VR role-play scenarios, and personalized guidance. Available on iOS and Android.
            </p>

            {/* Features */}
            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="flex items-start gap-4"
                  initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Download Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              {/* App Store Button */}
              <motion.button
                onClick={handleAppStoreDownload}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-black text-white rounded-2xl font-medium hover:bg-gray-800 transition-colors duration-200 min-w-[200px]"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                aria-label="Download Octify on the Apple App Store"
              >
                <AppleAppStoreIcon className="w-8 h-8" />
                <div className="text-left">
                  <div className="text-xs opacity-80">Download on the</div>
                  <div className="text-lg font-semibold">App Store</div>
                </div>
              </motion.button>

              {/* Google Play Button */}
              <motion.button
                onClick={handleGooglePlayDownload}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-black text-white rounded-2xl font-medium hover:bg-gray-800 transition-colors duration-200 min-w-[200px]"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                aria-label="Get Octify on Google Play Store"
              >
                <GooglePlayIcon className="w-8 h-8" />
                <div className="text-left">
                  <div className="text-xs opacity-80">Get it on</div>
                  <div className="text-lg font-semibold">Google Play</div>
                </div>
              </motion.button>
            </motion.div>

            {/* Stats */}
            <motion.div
              className="flex items-center gap-8 mt-8 pt-8 border-t border-gray-200/30 dark:border-gray-700/30"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">4.9</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">App Rating</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">50K+</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Downloads</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">94%</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Success Rate</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Phone Mockups */}
          <motion.div
            className="relative"
            initial={prefersReducedMotion ? {} : { opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="relative max-w-md mx-auto">
              {/* Main Phone */}
              <motion.div
                className="relative z-10"
                animate={prefersReducedMotion ? {} : {
                  y: [0, -10, 0],
                }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="bg-gray-900 rounded-[3rem] p-2 shadow-2xl">
                  <div className="bg-white rounded-[2.5rem] overflow-hidden">
                    <img
                      src="https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg"
                      alt="Octify mobile app interface showing AI-powered relationship insights and features"
                      className="w-full h-[600px] object-cover"
                    />
                    {/* App UI Overlay */}
                    <div className="absolute inset-2 bg-gradient-to-b from-transparent via-transparent to-black/20 rounded-[2.5rem] pointer-events-none" />
                  </div>
                </div>
              </motion.div>

              {/* Secondary Phone */}
              <motion.div
                className="absolute -right-8 top-16 z-0 opacity-60"
                animate={prefersReducedMotion ? {} : {
                  y: [0, 15, 0],
                }}
                transition={{ duration: 4, repeat: Infinity, delay: 2 }}
              >
                <div className="bg-gray-900 rounded-[2.5rem] p-2 shadow-xl scale-75">
                  <div className="bg-white rounded-[2rem] overflow-hidden">
                    <img
                      src="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg"
                      alt="Octify app features including VR role-play and relationship tracking"
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Floating Elements */}
              <motion.div
                className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full opacity-60 blur-sm"
                animate={prefersReducedMotion ? {} : {
                  y: [0, -20, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              
              <motion.div
                className="absolute -bottom-8 -right-8 w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-60 blur-sm"
                animate={prefersReducedMotion ? {} : {
                  y: [0, 15, 0],
                  scale: [1, 0.9, 1],
                }}
                transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
              />

              {/* Download Badge */}
              <motion.div
                className="absolute top-8 -left-8 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20 dark:border-gray-700/20"
                initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Download className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Free Download</div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">14-day trial included</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Trust Indicators */}
        <motion.div
          className="mt-20 text-center"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            Trusted by relationship experts and featured in
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            {['TechCrunch', 'Forbes', 'Psychology Today', 'Wired', 'The Verge'].map((publication, index) => (
              <motion.div
                key={publication}
                className="text-lg font-semibold text-gray-500 dark:text-gray-400"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
                whileInView={{ opacity: 0.6, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                viewport={{ once: true }}
              >
                {publication}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};